import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PublicLeadForm from "../components/PublicLeadForm";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { Briefcase, TrendingUp, Award, Users, ArrowDownRight, MapPin, ArrowUpRight } from "lucide-react";

export default function Employers() {
  useDocumentMeta({
    title:       "Employer Partnerships · Hire Pre-Certified Workforce | The Exchange Alliance",
    description: "Hire pre-certified, case-managed graduates from the Exchange Alliance workforce pipeline. OSHA-10, ServSafe, and trade credentials verified. 92% six-month retention. WOTC tax credits filed.",
    keywords:    "alliance-friendly employer, transitional workforce hiring, second-chance employer partner, WOTC, vocational pipeline, pre-certified workforce, the exchange alliance",
    canonical:   "https://theexchangealliance.com/employers",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main data-testid="employers-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans">
      <Header />

      <section className="pt-32 pb-16 sm:pt-40 sm:pb-24 border-b border-[#E8E5DA]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3 flex items-center gap-2">
            <Briefcase className="h-3 w-3"/> Employer Partnerships
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0612] leading-[1.05]">
            Hire the<br/>
            <span className="text-[#6B6F76]">most prepared</span><br/>
            workers in your region.
          </h1>
          <p className="max-w-2xl text-base sm:text-lg text-[#6B6F76] mt-6 leading-relaxed">
            Every Exchange Alliance graduate has cleared 12 weeks of vocational training, completed
            OSHA-10 + ServSafe certifications, holds a stable address, and is backed by a full case
            management team. Employers don't take a chance on our candidates — they hire <em>verified</em>.
            Every one of them is someone the world counted last, now first in line for your open role.
          </p>
        </div>
      </section>

      {/* 🚧 Featured Hiring Event — Myrtle Beach Earn & Learn (3/30/26).
          Promotional banner that surfaces just below the hero for any
          employer landing on this page. Artwork is self-contained (title +
          date + location baked in); we only add an outer frame, an event-
          meta strip, and a CTA that scrolls to the partnership intake. */}
      <section className="py-14 sm:py-20 bg-[#0A0612]" data-testid="featured-event-section">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <a
                href="#apply"
                data-testid="featured-event-image-link"
                className="block group relative overflow-hidden border-t-[3px] border-[#C9A227] shadow-[0_24px_60px_-20px_rgba(212,175,55,0.45)]"
                aria-label="Earn & Learn Construction Apprenticeship Hiring Event · Myrtle Beach"
              >
                <img
                  src="/earn-and-learn-myrtle-beach.png"
                  alt="Earn & Learn Construction Apprenticeship Hiring Event — Myrtle Beach"
                  className="block w-full h-auto transition-transform duration-[1200ms] ease-out group-hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                  width="1545"
                  height="784"
                />
                <span aria-hidden="true" className="absolute inset-0 ring-1 ring-inset ring-[#D4AF37]/25 pointer-events-none"/>
              </a>
            </div>
            <div className="lg:col-span-5 order-1 lg:order-2 text-white">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3 inline-flex items-center gap-2">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#86EFAC] opacity-75 animate-ping"/><span className="relative inline-flex rounded-full h-2 w-2 bg-[#22C55E]"/></span>
                Featured Hiring Event
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-white leading-tight">
                Earn &amp; Learn<br/>
                <span className="text-[#F4D88A]">Construction</span> Apprenticeship
              </h2>
              <p className="text-white/70 mt-4 text-base sm:text-lg leading-relaxed max-w-md">
                On-site interviews with pre-certified Alliance candidates. Verified OSHA-10, real-world ready,
                backed by a 90-day post-placement case manager. Booths still open.
              </p>
              <div className="mt-7">
                <div className="bg-white/[0.04] border border-white/10 px-4 py-3 inline-block" data-testid="event-meta-location">
                  <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/55 flex items-center gap-1.5"><MapPin className="h-3 w-3"/> Location</div>
                  <div className="font-display font-semibold text-base mt-1">Myrtle Beach, SC</div>
                </div>
              </div>
              <a
                href="#apply"
                data-testid="featured-event-cta"
                className="mt-7 inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] hover:bg-[#F4D88A] text-[#0A0612] font-mono text-[11px] tracking-[0.22em] uppercase transition-colors"
              >
                Reserve Employer Booth <ArrowUpRight className="h-4 w-4"/>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#E8E5DA] bg-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3">Why Local Businesses Partner With Us</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612] mb-12 max-w-2xl">A trained, screened workforce — backed by 250+ alliance agencies.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY.map((w) => (
              <article key={w.title} data-testid={`employer-pillar-${w.title.toLowerCase().replace(/\s+/g,'-')}`} className="bg-[#FAFAF7] border border-[#E8E5DA] p-6 lg:p-7" style={{ borderTop: `3px solid ${w.accent}` }}>
                <w.icon className="h-5 w-5 mb-4" style={{ color: w.accent }} strokeWidth={1.6}/>
                <h3 className="font-display font-bold text-lg tracking-tight text-[#0A0612] mb-2">{w.title}</h3>
                <p className="text-sm text-[#6B6F76] leading-relaxed">{w.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 border-b border-[#E8E5DA]">
        <div className="mx-auto max-w-6xl px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3">Track Record</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612] leading-tight">Quantified outcomes.<br/>Not feel-good metrics.</h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4 sm:gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="bg-[#0A0612] text-white p-5 sm:p-7" style={{ borderTop: `3px solid ${s.accent}` }}>
                <div className="font-display font-bold text-3xl sm:text-4xl tracking-tight" style={{ color: s.accent }}>{s.value}</div>
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/60 mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#C9A227] mb-3 inline-flex items-center gap-2">
              <ArrowDownRight className="h-3 w-3"/> Become an Employer Partner
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#0A0612]">Tell us about your open roles</h2>
          </div>
          <PublicLeadForm
            kind="employer_partnership"
            title="Employer Partnership Inquiry"
            subtitle="The Vocational Liaison will reach out within 2 business days to scope your hiring needs, share where candidates stand on their training and workforce readiness milestones, and walk through the alliance-friendly employer agreement."
            serviceOptions={[
              { value: "housing",             label: "Construction · Trades · Skilled Labor" },
              { value: "mental_health",       label: "Healthcare · Allied Health" },
              { value: "substance_support",   label: "Hospitality · Food Service" },
              { value: "food_security",       label: "Logistics · Warehousing" },
              { value: "employer_partnership",label: "Office · Administrative" },
              { value: "other",               label: "Other" },
            ]}
            accent="#1E40AF"
          />
        </div>
      </section>

      <Footer />
    </main>
  );
}

const WHY = [
  { icon: Award,      accent: "#C9A227", title: "Pre-certified",  body: "Every candidate clears OSHA-10, ServSafe, and trade-specific credentials before the first shift." },
  { icon: Users,      accent: "#7C3AED", title: "Backed by a team",body: "A case manager supports transportation, stability, and on-the-job success for the first 90 days on every placement." },
  { icon: TrendingUp, accent: "#1E40AF", title: "92% retention",   body: "Alliance graduates outperform regional retention benchmarks by more than 2× at the 6-month mark." },
  { icon: Briefcase,  accent: "#15803D", title: "Tax incentives",  body: "Most placements qualify for Work Opportunity Tax Credit (WOTC). We file the paperwork." },
];

const STATS = [
  { value: "$45–55K", label: "Avg starting wage",     accent: "#F4D88A" },
  { value: "92%",     label: "6-month retention",     accent: "#86EFAC" },
  { value: "250+",    label: "Partner agencies",      accent: "#C4B5FD" },
  { value: "159",     label: "Beds across Alliance",  accent: "#93C5FD" },
];
